/**
 * Live ETA display for CareVerify (PROMPT 10 / design memo §13).
 *
 * Used on SavoirCare Expected-column cards and the patient booking screen.
 * Prefers booking/entry.liveEta; falls back to declaredEta string.
 */

import React from "react";
import { Stack, Typography } from "@mui/material";
import AccessTimeRounded from "@mui/icons-material/AccessTimeRounded";

/**
 * @param {number} seconds
 * @returns {string}
 */
export function formatEtaSeconds(seconds) {
  const s = Math.max(0, Math.round(Number(seconds) || 0));
  if (s < 60) return "Under 1 min";
  const mins = Math.round(s / 60);
  if (mins < 60) return `~${mins} min`;
  const hours = Math.floor(mins / 60);
  const rem = mins % 60;
  return rem ? `~${hours}h ${rem}m` : `~${hours}h`;
}

/**
 * @param {object} props
 * @param {{ currentEstimateSeconds?: number, travelMode?: string, arrived?: boolean, trackingStopped?: boolean }|null} [props.liveEta]
 * @param {string|null} [props.declaredEta]
 * @param {'compact'|'detailed'} [props.variant]
 */
const LiveEtaDisplay = ({
  liveEta = null,
  declaredEta = null,
  variant = "compact",
}) => {
  if (liveEta?.arrived) {
    return (
      <Stack
        direction="row"
        spacing={0.5}
        alignItems="center"
        data-testid="careverify-live-eta"
      >
        <AccessTimeRounded fontSize="inherit" color="success" />
        <Typography variant="caption" color="success.main">
          Arrived
        </Typography>
      </Stack>
    );
  }

  const hasLive =
    liveEta &&
    Number.isFinite(Number(liveEta.currentEstimateSeconds)) &&
    !liveEta.trackingStopped;

  if (hasLive) {
    const label = formatEtaSeconds(liveEta.currentEstimateSeconds);
    const mode =
      liveEta.travelMode && variant === "detailed"
        ? ` · ${liveEta.travelMode}`
        : "";
    return (
      <Stack
        direction="row"
        spacing={0.5}
        alignItems="center"
        data-testid="careverify-live-eta"
      >
        <AccessTimeRounded fontSize="inherit" color="action" />
        <Typography variant="caption" color="text.secondary">
          ETA {label}
          {mode}
        </Typography>
      </Stack>
    );
  }

  if (declaredEta) {
    return (
      <Stack
        direction="row"
        spacing={0.5}
        alignItems="center"
        data-testid="careverify-declared-eta"
      >
        <AccessTimeRounded fontSize="inherit" color="action" />
        <Typography variant="caption" color="text.secondary">
          Declared ETA: {declaredEta}
        </Typography>
      </Stack>
    );
  }

  return null;
};

export default LiveEtaDisplay;
